import { Fragment, useEffect, useState } from "react";
import AppText from "../components/app-text";
import BaseService from "../service/base.service";
import { OffreService } from "../service/offre.service";
import ActualiteService from "../service/actualite.service";
import Contact from "../contact";
import { useLocation, Link, useNavigate } from "react-router-dom";
import SearchForm from "../search-form";
import Gallery from "../components/gallery";

const service = new BaseService("InformationHotel");
const gastronomieService = new BaseService("Gastronomie");
const conferenceService = new BaseService("SalleConference");
const spaService = new BaseService("MediaSpa");
const actualiteService = new ActualiteService();

// ROOMS SECTION - Horizontal Cards Layout
const RoomsPreviewSection = ({ items }) => {
  const [lng, setLng] = useState(null);
  useEffect(() => {
    setLng(localStorage.getItem("lng"));
  }, []);
  return (
    <section className="rooms-preview-section py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="rooms-section-title">
              <AppText cle="rooms_section_title" section="sections_accueil" />
            </h2>
            <p className="rooms-section-subtitle">
              <AppText cle="rooms_section_subtitle" section="sections_accueil" />
            </p>
          </div>
        </div>
        <div className="rooms-horizontal-layout">
          {items.slice(0, 3).map((room, index) => (
            <div key={room.id} className="room-horizontal-card mb-4">
              <div className="row g-0">
                <div className="col-md-5">
                  <div className="room-image-container">
                    <img
                      src={room.image_hebergements?.[0]?.image || '/assets/img/placeholder.jpg'}
                      alt={room.categorie}
                      className="room-horizontal-image"
                    />
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="room-horizontal-content">
                    <h3 className="room-horizontal-title">{room[`categorie${lng === "fr" ? "" : "_" + lng}`]}</h3>
                    <p className="room-horizontal-description">
                      {room[`description${lng === "fr" ? "" : "_" + lng}`]}
                    </p>
                    <div className="room-features">
                      <span className="feature-item">
                        <i className="bi bi-wifi"></i> <AppText cle="wifi_gratuit" section="sections_accueil" />
                      </span>
                      <span className="feature-item">
                        <i className="bi bi-tv"></i> <AppText cle="tv_hd" section="sections_accueil" />
                      </span>
                      <span className="feature-item">
                        <i className="bi bi-wind"></i> <AppText cle="climatisation" section="sections_accueil" />
                      </span>
                    </div>
                    <div className="room-pricing">
                      <span className="room-price">
                        <AppText cle="a_partir_de" section="sections_accueil" /> {room.debut_prix}DT
                      </span>
                      <span className="room-period">
                        <AppText cle="par_nuit" section="sections_accueil" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-4">
          <Link to="/rooms" className="rooms-cta-btn">
            <i className="bi bi-arrow-right"></i> <AppText cle="voir_toutes_chambres" section="sections_accueil" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Restaurants Preview Section with Gastros Page Style
const RestaurantsPreviewSection = ({ restaurants }) => {
  // Function to get the AppText key for each restaurant
  const getRestaurantKey = (restaurantLabel) => {
    const labelMap = {
      'Eight Club': 'restaurant_eight_club',
      'Ted & Co Bar': 'restaurant_ted_co_bar',
      'Swiss Flavours': 'restaurant_swiss_flavours'
    };
    return labelMap[restaurantLabel] || null;
  };

  if (!restaurants || restaurants.length === 0) {
    return null;
  }

  return (
    <section className="restaurants-preview-section">
      <div className="container py-5">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="restaurants-section-title">
              <AppText cle="restaurants_section_title" section="sections_accueil" />
            </h2>
            <p className="restaurants-section-subtitle">
              <AppText cle="restaurants_section_subtitle" section="sections_accueil" />
            </p>
          </div>
        </div>
        
        <div className="restaurants-gastros-grid">
          {restaurants.slice(0, 4).map((restaurant, index) => (
            <div key={index} className="restaurant-gastros-item">
              <div className="restaurant-gastros-image">
                <img 
                  src={restaurant.image || '/images/default-restaurant.jpg'} 
                  alt={restaurant.label}
                />
                <div className="restaurant-gastros-overlay">
                  <div className="restaurant-gastros-info">
                    <div className="restaurant-dates">
                      <span className="info-label">
                        <AppText cle="dates_ouverture" section="sections_accueil" />
                      </span>
                      <span className="info-value">
                        <AppText cle="lundi_dimanche" section="sections_accueil" />
                      </span>
                    </div>
                    <div className="restaurant-hours">
                      <span className="info-label">
                        <AppText cle="horaires_ouverture" section="sections_accueil" />
                      </span>
                      <span className="info-value">07H00 - 01H00</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="restaurant-gastros-title">
                <h3>
                  {getRestaurantKey(restaurant.label) ? (
                    <AppText cle={getRestaurantKey(restaurant.label)} />
                  ) : (
                    restaurant.label
                  )}
                </h3>
              </div>
            </div>
          ))}
        </div>
        
        <div className="row mt-5">
          <div className="col-12 text-center">
            <a href="/gastros" className="restaurants-cta-btn">
              <i className="bi bi-arrow-right"></i> <AppText cle="voir_tous_restaurants" section="sections_accueil" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// CONFERENCES SECTION - List/Table Layout
const ConferencesPreviewSection = ({ items }) => {
  return (
    <section className="conferences-preview-section py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="conferences-section-title">
              <AppText cle="conferences_section_title" section="sections_accueil" />
            </h2>
            <p className="conferences-section-subtitle">
              <AppText cle="conferences_section_subtitle" section="sections_accueil" />
            </p>
          </div>
        </div>
        
        <div className="conferences-list-layout">
          <div className="conferences-header">
            <h4>
              <AppText cle="nos_salles_disponibles" section="sections_accueil" />
            </h4>
            <p>
              <AppText cle="equipements_services_pro" section="sections_accueil" />
            </p>
          </div>
          
          <div className="conferences-table">
            {items.slice(0, 3).map((conference, index) => (
              <div key={conference.id} className="conference-row">
                <div className="conference-image-cell">
                  <img 
                    src={conference.image || '/assets/img/placeholder.jpg'} 
                    alt={conference.label || `Salle ${index + 1}`}
                    className="conference-thumbnail"
                  />
                </div>
                <div className="conference-info-cell">
                  <h5 className="conference-name">{conference.label || `Salle ${index + 1}`}</h5>
                  <div className="conference-details">
                    {conference.theatre && (
                      <div className="detail-item">
                        <i className="bi bi-people"></i>
                        <span>
                          <AppText cle="theatre" section="sections_accueil" />: {conference.theatre} <AppText cle="personnes" section="sections_accueil" />
                        </span>
                      </div>
                    )}
                    {conference.class_room && (
                      <div className="detail-item">
                        <i className="bi bi-laptop"></i>
                        <span>
                          <AppText cle="classe" section="sections_accueil" />: {conference.class_room} <AppText cle="personnes" section="sections_accueil" />
                        </span>
                      </div>
                    )}
                    {conference.tables_rondes && (
                      <div className="detail-item">
                        <i className="bi bi-circle"></i>
                        <span>
                          <AppText cle="tables_rondes" section="sections_accueil" />: {conference.tables_rondes} <AppText cle="personnes" section="sections_accueil" />
                        </span>
                      </div>
                    )}
                    {conference.cocktails && (
                      <div className="detail-item">
                        <i className="bi bi-cup-straw"></i>
                        <span>
                          <AppText cle="cocktail" section="sections_accueil" />: {conference.cocktails} <AppText cle="personnes" section="sections_accueil" />
                        </span>
                      </div>
                    )}
                    {conference.superficie && (
                      <div className="detail-item">
                        <i className="bi bi-rulers"></i>
                        <span>
                          <AppText cle="superficie" section="sections_accueil" />: {conference.superficie}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-5">
          <Link to="/conferences" className="conferences-cta-btn">
            <i className="bi bi-arrow-right"></i> <AppText cle="voir_tous_espaces" section="sections_accueil" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// SPA SECTION - Gallery Grid Layout
const SpaPreviewSection = ({ items }) => {
  return (
    <section className="spa-preview-section py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="spa-section-title">
              <AppText cle="spa_section_title" section="sections_accueil" />
            </h2>
            <p className="spa-section-subtitle">
              <AppText cle="spa_section_subtitle" section="sections_accueil" />
            </p>
          </div>
        </div>
        
        <div className="spa-gallery-grid">
          <div className="spa-main-content">
            <div className="spa-featured-image">
              {items.length > 0 && (
                <img 
                  src={items[0].src || '/assets/img/placeholder.jpg'} 
                  alt="Spa principal"
                  className="spa-hero-image"
                />
              )}
              <div className="spa-hero-overlay">
                <h3>
                  <AppText cle="detente_absolue" section="sections_accueil" />
                </h3>
                <p>
                  <AppText cle="massages_soins_visage" section="sections_accueil" />
                </p>
              </div>
            </div>
            
            <div className="spa-services-list">
              <h4>
                <AppText cle="nos_services" section="sections_accueil" />
              </h4>
              <div className="spa-service-item">
                <i className="bi bi-flower1"></i>
                <span>
                  <AppText cle="massages_relaxants" section="sections_accueil" />
                </span>
              </div>
              <div className="spa-service-item">
                <i className="bi bi-droplet"></i>
                <span>
                  <AppText cle="soins_hydratants" section="sections_accueil" />
                </span>
              </div>
              <div className="spa-service-item">
                <i className="bi bi-sun"></i>
                <span>
                  <AppText cle="therapies_lumineuses" section="sections_accueil" />
                </span>
              </div>
              <div className="spa-service-item">
                <i className="bi bi-heart"></i>
                <span>
                  <AppText cle="soins_bien_etre" section="sections_accueil" />
                </span>
              </div>
            </div>
          </div>
          
          <div className="spa-image-mosaic">
            {items.slice(1, 5).map((item, index) => (
              <div key={index} className={`spa-mosaic-item spa-item-${index + 1}`}>
                <img 
                  src={item.src || '/assets/img/placeholder.jpg'} 
                  alt={`Spa ${index + 1}`}
                />
                <div className="spa-mosaic-overlay">
                  <i className="bi bi-plus"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="row mt-5">
          <div className="col-12 text-center">
            <a href="/spas" className="spa-cta-btn">
              <i className="bi bi-arrow-right"></i> <AppText cle="voir_notre_spa" section="sections_accueil" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// OFFRES SECTION - Homepage Banner Layout (728x90px banners, 500 char text)
const OffresPreviewSection = ({ offres }) => {
  const [lng, setLng] = useState(null);
  const [expandedOffres, setExpandedOffres] = useState(new Set()); // Track expanded offers

  useEffect(() => {
    setLng(localStorage.getItem("lng"));
  }, []);

  // Helper function to get title in current language
  const getOffreTitle = (offre) => {
    return offre[`title_${lng}`] || offre.title_fr || `Offre ${offre.id}`;
  };

  // Helper function to get content in current language
  const getOffreContent = (offre) => {
    return offre[`content_${lng}`] || offre.content_fr || "";
  };

  // Helper function to get rich content in current language
  const getOffreRichContent = (offre) => {
    return offre[`contenu_${lng}`] || offre.contenu_fr || "";
  };

  // Toggle rich content visibility
  const toggleRichContent = (offreId) => {
    const newExpanded = new Set(expandedOffres);
    if (newExpanded.has(offreId)) {
      newExpanded.delete(offreId);
    } else {
      newExpanded.add(offreId);
    }
    setExpandedOffres(newExpanded);
  };

  // Check if an offer has rich content
  const hasRichContent = (offre) => {
    return getOffreRichContent(offre).trim().length > 0;
  };

  if (!offres || offres.length === 0 || lng === null) {
    return null;
  }

  return (
    <section className="offres-preview-section py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="offres-section-title">
              <AppText cle="offres_titre" section="sections_offres" />
            </h2>
            <p className="offres-section-subtitle">
              <AppText cle="offres_sous_titre" section="sections_offres" />
            </p>
          </div>
        </div>
        
        <div className="row">
          {offres.slice(0, 4).map((offre, index) => (
            <div key={offre.id} className="col-md-6 mb-4">
              <div className="offre-homepage-card h-100">
                <div className="offre-card-content">
                  {/* Image Section */}
                  <div className="offre-homepage-banner mb-3">
                    {offre.image ? (
                      <img 
                        src={offre.image}
                        alt={getOffreTitle(offre)}
                        style={{
                          width: "100%",
                          height: "180px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "2px solid var(--primary-color)"
                        }}
                      />
                    ) : (
                      <div 
                        style={{
                          width: "100%",
                          height: "180px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "2px dashed var(--primary-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--primary-color)"
                        }}
                      >
                        <span>Image offre</span>
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="offre-homepage-content">
                    <h3 className="offre-homepage-title mb-3">{getOffreTitle(offre)}</h3>
                    <p className="offre-homepage-description" style={{ fontSize: "1.1rem", lineHeight: "1.5" }}>
                      {(() => {
                        const content = getOffreContent(offre);
                        if (content && content.length <= 300) {
                          return content;
                        } else if (content) {
                          return content.substring(0, 300) + "...";
                        } else {
                          return "Description de l'offre disponible sur la page dédiée.";
                        }
                      })()}
                    </p>
                    <div className="offre-homepage-actions mt-3">
                      {(offre.type === 'accommodation' || offre.is_accommodation) && (
                        <button 
                          className="btn btn-primary me-2"
                          style={{
                            backgroundColor: "var(--primary-color)",
                            borderColor: "var(--primary-color)"
                          }}
                          onClick={() =>
                            window.open(
                              "https://direct-book.com/properties/HotelRoyalASBUTunisDIRECT",
                              "_blank"
                            )
                          }
                        >
                          <i className="bi bi-calendar-check me-2"></i>
                          <AppText cle="reserver" section="sections_offres" />
                        </button>
                      )}
                      
                      {/* Plus d'info button - only show if there's rich content */}
                      {hasRichContent(offre) && (
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={() => toggleRichContent(offre.id)}
                        >
                          <i className={`bi ${expandedOffres.has(offre.id) ? 'bi-eye-slash' : 'bi-info-circle'} me-2`}></i>
                          <AppText cle={expandedOffres.has(offre.id) ? "moins_info" : "plus_info"} />
                        </button>
                      )}
                    </div>

                    {/* Rich Content Section - Show when expanded */}
                    {expandedOffres.has(offre.id) && hasRichContent(offre) && (
                      <div className="rich-content-section mt-3 p-3" style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9ecef"
                      }}>
                        <h6 className="mb-2" style={{ color: "var(--primary-color)" }}>
                          <i className="bi bi-info-circle me-2"></i>
                          <AppText cle="details_offre" />
                        </h6>
                        <div 
                          className="rich-content"
                          dangerouslySetInnerHTML={{ __html: getOffreRichContent(offre) }}
                          style={{
                            fontSize: "0.95rem",
                            lineHeight: "1.5",
                            color: "#495057"
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-4">
          <Link to="/offres" className="offres-voir-plus-btn">
            <i className="bi bi-arrow-right me-2"></i>
            <AppText cle="voir_plus" section="sections_offres" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ACTUALITÉS SECTION - News Layout with Website Colors
const ActualitesPreviewSection = ({ actualites }) => {
  const [lng, setLng] = useState(null);

  useEffect(() => {
    setLng(localStorage.getItem("lng"));
  }, []);

  // Helper function to get title in current language
  const getActualiteTitle = (actualite) => {
    if (lng === null) return "";
    return actualite[`title_${lng}`] || actualite.title_fr || actualite.title_en || actualite.title_ar || `Actualité ${actualite.id}`;
  };

  // Helper function to get summary in current language
  const getActualiteSummary = (actualite) => {
    if (lng === null) return "";
    const summary = actualite[`summary_${lng}`] || actualite.summary_fr || actualite.summary_en || actualite.summary_ar || "";
    const content = actualite[`content_${lng}`] || actualite.content_fr || actualite.content_en || actualite.content_ar || "";
    
    if (summary) {
      return summary.length > 120 ? summary.substring(0, 120) + "..." : summary;
    } else if (content) {
      return content.substring(0, 120) + "...";
    }
    return "";
  };

  if (!actualites || actualites.length === 0 || lng === null) {
    return null;
  }

  return (
    <section className="actualites-preview-section py-5" style={{
      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
    }}>
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="actualites-section-title" style={{
              color: "var(--secondary-color)",
              fontFamily: "'Cormorant Serif', serif",
              fontSize: "2.5rem",
              fontWeight: "700",
              marginBottom: "15px"
            }}>
              <AppText cle="actualites_titre" section="sections_actualites" />
            </h2>
            <p className="actualites-section-subtitle" style={{
              color: "#6c757d",
              fontSize: "1.4rem",
              maxWidth: "600px",
              margin: "0 auto"
            }}>
              <AppText cle="actualites_sous_titre" section="sections_actualites" />
            </p>
          </div>
        </div>
        
        <div className="row">
          {actualites.slice(0, 3).map((actualite, index) => (
            <div key={actualite.id} className="col-lg-4 col-md-6 mb-4">
              <div className="actualite-vertical-card" style={{
                background: "white",
                borderRadius: "15px",
                overflow: "hidden",
                boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}>
                
                {/* Date Badge */}
                <div style={{
                  position: "absolute",
                  top: "20px",
                  left: "20px",
                  background: "var(--primary-color)",
                  color: "white",
                  padding: "8px 15px",
                  borderRadius: "20px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  zIndex: "2",
                  boxShadow: "0 3px 10px rgba(214, 171, 95, 0.3)"
                }}>
                  <i className="bi bi-calendar3 me-2"></i>
                  {new Date(actualite.createdAt).toLocaleDateString('fr-FR', { 
                    day: 'numeric',
                    month: 'short'
                  })}
                </div>

                {/* Banner Section */}
                <div style={{
                  height: "220px",
                  background: `linear-gradient(135deg, 
                    ${index === 0 ? 'var(--primary-color), rgba(214, 171, 95, 0.8)' : 
                      index === 1 ? 'var(--secondary-color), rgba(71, 84, 80, 0.8)' : 
                      'rgba(214, 171, 95, 0.9), rgba(71, 84, 80, 0.9)'}
                  )`,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  {actualite.banner_300x600 ? (
                    <img
                      src={actualite.banner_300x600}
                      alt={getActualiteTitle(actualite)}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <div style={{
                      textAlign: "center",
                      color: "white"
                    }}>
                      <i className="bi bi-newspaper" style={{ fontSize: "3rem", marginBottom: "10px" }}></i>
                      <p style={{ margin: "0", fontSize: "0.9rem", opacity: "0.9" }}>
                        Banner 300x600
                      </p>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div style={{
                  padding: "25px",
                  flex: "1",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <h3 style={{
                    color: "var(--secondary-color)",
                    fontSize: "1.3rem",
                    fontWeight: "600",
                    marginBottom: "15px",
                    lineHeight: "1.3",
                    fontFamily: "'Cormorant Serif', serif"
                  }}>
                    {getActualiteTitle(actualite)}
                  </h3>
                  
                  <p style={{
                    color: "#6c757d",
                    fontSize: "1rem",
                    lineHeight: "1.6",
                    marginBottom: "20px",
                    flex: "1"
                  }}>
                    {getActualiteSummary(actualite)}
                  </p>

                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "auto"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      color: "#adb5bd",
                      fontSize: "0.9rem"
                    }}>
                      <i className="bi bi-clock me-2"></i>
                      <span>
                        {new Date(actualite.createdAt).toLocaleDateString('fr-FR', { 
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </span>
                    </div>

                    {/* Voir plus Button - Matching spa-cta-btn style */}
                    <Link 
                      to="/actualites"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "linear-gradient(45deg, var(--primary-color), rgba(214, 171, 95, 0.8))",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "25px",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 15px rgba(214, 171, 95, 0.3)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 6px 20px rgba(214, 171, 95, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0px)";
                        e.target.style.boxShadow = "0 4px 15px rgba(214, 171, 95, 0.3)";
                      }}
                    >
                      <AppText cle="voir_plus" section="sections_actualites" />
                      <i className="bi bi-arrow-right ms-2"></i>
                    </Link>
                  </div>
                </div>

                {/* Bottom Accent Line */}
                <div style={{
                  height: "4px",
                  background: `linear-gradient(90deg, 
                    ${index === 0 ? 'var(--primary-color), rgba(214, 171, 95, 0.6)' : 
                      index === 1 ? 'var(--secondary-color), rgba(71, 84, 80, 0.6)' : 
                      'var(--primary-color), var(--secondary-color)'}
                  )`
                }} />
              </div>
            </div>
          ))}
        </div>
        
        {/* Main "Voir plus" Button - Matching spa-cta-btn style */}
        <div className="text-center mt-5">
          <Link 
            to="/actualites"
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "linear-gradient(45deg, var(--primary-color), rgba(214, 171, 95, 0.8))",
              color: "white",
              padding: "15px 30px",
              borderRadius: "30px",
              textDecoration: "none",
              fontSize: "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 5px 20px rgba(214, 171, 95, 0.3)"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-3px)";
              e.target.style.boxShadow = "0 10px 30px rgba(214, 171, 95, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0px)";
              e.target.style.boxShadow = "0 5px 20px rgba(214, 171, 95, 0.3)";
            }}
          >
            <i className="bi bi-newspaper me-3" style={{ fontSize: "1.2rem" }}></i>
            <AppText cle="voir_plus" section="sections_actualites" />
          </Link>
        </div>
      </div>

      {/* CSS for hover effects */}
      <style jsx>{`
        .actualite-vertical-card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 15px 35px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </section>
  );
};

export default function Acceuil() {
  const [landingData, setLandingData] = useState();
  const [gastronomieData, setGastronomieData] = useState([]);
  const [conferenceData, setConferenceData] = useState([]);
  const [spaData, setSpaData] = useState([]);
  const [offresData, setOffresData] = useState([]);
  const [actualitesData, setActualitesData] = useState([]);
  const pathName = useLocation();
  const navigate = useNavigate();
  const [galleryLoaded, setGalleryLoaded] = useState(false);
  const [contactLoaded, setContactLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const section = document.getElementById(pathName.hash.slice(1));
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  }, [pathName, galleryLoaded, contactLoaded]);

  useEffect(() => {
    // Fetch landing data (includes rooms and some gastronomy)
    service.getLandingData().then((res) => {
      setLandingData(res);
    });

    // Fetch additional data for each section
    gastronomieService.getAll().then((res) => {
      setGastronomieData(res);
    });

    conferenceService.getAll().then((res) => {
      setConferenceData(res);
    });

    spaService.getAll().then((res) => {
      setSpaData(res.filter(item => item.type === "image"));
    });

    // Fetch offers data
    OffreService.getActive().then((res) => {
      setOffresData(res);
    }).catch((error) => {
      console.error("Error loading offers for homepage:", error);
      setOffresData([]);
    });

    // Fetch news data
    actualiteService.getActiveActualites().then((res) => {
      setActualitesData(res);
    }).catch((error) => {
      console.error("Error loading news for homepage:", error);
      setActualitesData([]);
    });
  }, []);

  if (!landingData)
    return (
      <p>
        <AppText cle="chargement" />
      </p>
    );

  return (
    <Fragment>
      <section id="hero">
        {showVideo && (
          <div className="app-modal-container" style={{ zIndex: 9999 }}>
            <div
              className="app-modal-content"
              style={{ position: "relative", width: "60%" }}
            >
              <button
                style={{
                  position: "absolute",
                  top: 30,
                  right: 30,
                  background: "white",
                  outline: "none",
                  border: "none",
                  width: 40,
                  height: 40,
                  borderRadius: "50%!important",
                  zIndex: 9999,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVideo(false);
                }}
              >
                x
              </button>
              <video width="100%" height="auto" controls autoPlay>
                <source src={landingData.sectionVideo.video} type="video/mp4" />
              </video>
            </div>
          </div>
        )}
        <div className="hero-container">
          <div
            id="heroCarousel"
            data-bs-interval="5000"
            className="carousel slide carousel-fade"
            data-bs-ride="carousel"
          >
            <div className="carousel-inner" role="listbox">
              <div
                className="carousel-item active"
                style={{
                  backgroundImage: `url(${landingData.informationsHotel.image_acceuil})`,
                }}
              >
                <div className="carousel-container">
                  <div className="carousel-content">
                    <h2 className="animate__animated animate__fadeInDown">
                      <AppText cle="text_acceuil" />
                      <img
                        height={120}
                        src="assets/img/logo-Royal-ASBU.png"
                        alt=""
                        onClick={() => navigate("/")}
                        style={{ cursor: "pointer" }}
                      />
                    </h2>
                    <p className="animate__animated animate__fadeInUp">
                      {landingData.informationsHotel.presentation}
                    </p>
                  </div>
                </div>
              </div>
              <SearchForm />
            </div>
          </div>
        </div>
      </section>
      <main id="main">
        <section id="about" className="about">
          <div className="container-fluid">
            <div className="row">
              <div
                className="col-lg-5 align-items-stretch video-box"
                style={{ backgroundImage: `url('assets/img/IMAGEREPLACED.jpeg')` }}
              >
                <a
                  href="#"
                  className="venobox play-btn mb-4"
                  onClick={() => setShowVideo(true)}
                />
              </div>

              <div className="col-lg-7 d-flex flex-column justify-content-center align-items-stretch">
                <div className="content">
                  <h3>
                    <AppText cle="titre video" />
                  </h3>
                  <p>
                    <AppText cle="text video" />
                  </p>

                  <ul>
                    {landingData.sectionVideoBullets.map((b) => (
                      <li key={b.id}>
                        <i className="bx bx-check-double"></i>
                        {b.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Preview Sections - Each with UNIQUE Structure */}
        <section id="services-preview" className="services-preview">
          <div className="container">
            {/* Title section removed */}
          </div>

          {/* Rooms Section - Horizontal Cards */}
          {landingData.chambres && landingData.chambres.length > 0 && (
            <RoomsPreviewSection items={landingData.chambres} />
          )}

          {/* Restaurants Section - Gastros Page Style */}
          {gastronomieData && gastronomieData.length > 0 && (
            <RestaurantsPreviewSection restaurants={gastronomieData} />
          )}

          {/* Conferences Section - List/Table */}
          {conferenceData && conferenceData.length > 0 && (
            <ConferencesPreviewSection items={conferenceData} />
          )}

          {/* Spa Section - Gallery Grid */}
          {spaData && spaData.length > 0 && (
            <SpaPreviewSection items={spaData} />
          )}

          {/* OFFRES Section - Homepage Banner Layout */}
          {offresData && offresData.length > 0 && (
            <OffresPreviewSection offres={offresData} />
          )}

          {/* ACTUALITÉS Section */}
          {actualitesData && actualitesData.length > 0 && (
            <ActualitesPreviewSection actualites={actualitesData} />
          )}
        </section>

        {/*    why choose us*/}
        {/* <section id="why-us" className="why-us">
          <div className="container">
            <div className="section-title">
              <h2>
                <AppText cle={"pnc_titre"} />{" "}
                <img height={90} src="assets/img/logo-Royal-ASBU.png" alt="" />
              </h2>
              <p>
                <AppText cle={"pnc_sous_titre"} />
              </p>
            </div>

            <div className="row">
              <div className="col-lg-4">
                <div className="box">
                  <span>01</span>
                  <h4>
                    <AppText cle={"pnc_t1"} />
                  </h4>
                  <p>
                    <AppText cle={"pnc_d1"} />
                  </p>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="box">
                  <span>02</span>
                  <h4>
                    <AppText cle={"pnc_t2"} />
                  </h4>
                  <p>
                    <AppText cle={"pnc_d2"} />
                  </p>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="box">
                  <span>03</span>
                  <h4>
                    <AppText cle={"pnc_t3"} />
                  </h4>
                  <p>
                    <AppText cle={"pnc_d3"} />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section> */}
        {/*    gallery*/}
        <section
          onLoad={() => setGalleryLoaded(true)}
          id="gallery"
          className="gallery"
        >
          <div className="container-fluid">          <div className="section-title">
            <h2>
              <AppText cle="photos" />{" "}
              <img 
                height={90} 
                src="assets/img/logo-Royal-ASBU.png" 
                alt="" 
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
              />
            </h2>
            <p className="text-center" style={{
              color: "#6c757d",
              fontSize: "1.4rem",
              maxWidth: "600px",
              margin: "0 auto"
            }}>
              <AppText cle="photo_st" />
            </p>
          </div>
          </div>
          <Gallery
            imageHeight={350}
            images={[
              "assets/img/g1.webp",
              "assets/img/spa_gallery.jpg",
              "assets/img/resto_gallery.jpg",
              "assets/img/room-gallery.jfif",
              "assets/img/welcome.jfif",
              "assets/img/g6.webp",
              "assets/img/reception_spa_gallery.jpg",
              "assets/img/spa2_gallery.jpg",
              "assets/img/g7.webp",
            ]}
          />
        </section>

        {/*    contact*/}
        <Contact
          onLoad={() => setContactLoaded(true)}
          data={landingData.informationsHotel}
          service={service}
        />
      </main>
    </Fragment>
  );
}
